import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import SelectWithTags from '@/components/ui/select-with-tags'
import { toastHandler } from '@/components/utils/Toast'
import { useAuthContext } from '@/context'
import { useProcessDocumentAccessRequest, useUpdateDocumentPermissions } from '@/services/folders/mutations'
import { keyDocument, useQueryDocumentAccessRequests } from '@/services/folders/queries'
import { DocumentInterface } from '@/services/folders/types'
import { useQueryInvites } from '@/services/users/queries'
import { capitalCase } from '@/utils/string'
import { AlertCircle, ChevronDown, ChevronUp, Edit, Eye, Info, Lock, Users, Users2, X } from 'lucide-react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router'

interface InviteListInterface {
    id: string
    name: string
    email: string
    role?: string
}

type FormValues = {
    roles: string[]
    accounts: { label: string; value: string }[]
}

type Props = {
    trigger: ReactNode
    onClose?: () => void
    document: DocumentInterface
    insideDropdown?: boolean
    setShowActions?: (showActions: boolean) => void
    defaultOpen?: boolean
}

const fileAccessRoles = ['viewer', 'editor']

const ShareFile = ({ trigger, document, insideDropdown, setShowActions, defaultOpen = false, onClose }: Props) => {
    const { orgAccount, user, org } = useAuthContext()
    const location = useLocation()
    const navigate = useNavigate()
    const [open, setOpen] = useState(defaultOpen)
    const [inviteList, setInviteList] = useState<InviteListInterface[]>([])
    const [sharingType, setSharingType] = useState<'workspace' | 'individual' | 'none'>(
        document.shared_with_workspace?.enabled ? 'workspace' : document.permissions?.length > 1 ? 'individual' : 'none'
    )
    const [workspaceRole, setWorkspaceRole] = useState(document.shared_with_workspace?.role || 'viewer')
    const [forceInherit, setForceInherit] = useState(false)
    const [showShareDetails, setShowShareDetails] = useState(false)
    const [requesterAccountId, setRequesterAccountId] = useState<string | null>(null)
    const [isFromAccessRequest, setIsFromAccessRequest] = useState(false)

    const methods = useForm<FormValues>()

    const {
        control,
        handleSubmit,
        formState: { isValid },
        setValue,
    } = methods

    const { data: invites } = useQueryInvites({
        enabled: !!orgAccount?.org?.id,
        queryParams: {
            org: orgAccount?.org?.id as string,
        },
    })

    // Fetch pending access requests for this document
    const { data: accessRequests } = useQueryDocumentAccessRequests({
        enabled: !!document.id && isFromAccessRequest,
        queryParams: {
            document_id: document.id,
        },
    })

    const { mutate: processAccessRequest } = useProcessDocumentAccessRequest({})

    const { mutate: shareFile, isLoading: sharing } = useUpdateDocumentPermissions({
        refetch: [keyDocument(``)],
        onSuccess: () => {
            // If this was from an access request, approve the request
            if (isFromAccessRequest && requesterAccountId && accessRequests) {
                const pendingRequest = accessRequests.find(
                    (req: any) => req.requester === requesterAccountId && req.status === 'pending'
                )

                if (pendingRequest) {
                    processAccessRequest(
                        {
                            request_id: pendingRequest._id,
                            action: 'approve',
                        },
                        {
                            onSuccess: () => {
                                console.log('Access request approved successfully')
                            },
                            onError: error => {
                                console.error('Failed to approve access request:', error)
                            },
                        }
                    )
                }
            }

            setOpen(false)
            setShowActions && setShowActions(false)

            // Clean up URL parameters after successful save
            const searchParams = new URLSearchParams(location.search)
            if (searchParams.has('openShare') || searchParams.has('requester')) {
                searchParams.delete('openShare')
                searchParams.delete('requester')
                const newSearch = searchParams.toString()
                const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`
                navigate(newUrl, { replace: true })
            }

            toastHandler({
                message: 'File permissions updated',
            })
        },
    })

    const membersData = useMemo(
        () =>
            invites?.filter(invite => ['accepted', 'pending'].includes(invite.status) && invite?.email !== user?.email) ||
            [],
        [invites, user?.email]
    )

    const handleRoleChange = (inviteIndex: number, newRole: string) => {
        setInviteList(currentList =>
            currentList.map((invite, index) => (index === inviteIndex ? { ...invite, role: newRole } : invite))
        )
    }

    const onSubmit = () => {
        if (sharingType === 'workspace') {
            const payload = {
                id: document.id,
                permissions: [],
                shared_with_workspace: {
                    enabled: true,
                    role: workspaceRole,
                },
                org: org?.id,
                account: orgAccount?.id,
                forceInherit,
            }
            shareFile(payload)
        } else {
            const updatedInviteList = inviteList.map(invite => ({
                account: invite.id,
                role: invite.role,
            }))

            const payload = {
                id: document.id,
                permissions: updatedInviteList,
                shared_with_workspace: {
                    enabled: false,
                },
                org: org?.id,
                account: orgAccount?.id,
                forceInherit,
            }
            shareFile(payload)
        }
    }

    const getRoleDescription = (role: string) => {
        switch (role) {
            case 'viewer':
                return 'Can view document'
            case 'editor':
                return 'Can edit document'
            default:
                return 'Select an access level'
        }
    }

    useEffect(() => {
        const accounts = membersData
            .filter(member => document.permissions?.find(permission => permission.account === member.id))
            .map(member => {
                return {
                    id: member.id,
                    name: member.user?.fullname,
                    email: member.email,
                    role: document.permissions?.find(permission => permission.account === member.id)?.role as string,
                }
            })

        setInviteList(accounts)
        setValue(
            'accounts',
            accounts.map(account => ({ label: account.name, value: account.id }))
        )
    }, [document, invites, membersData, setValue])

    // Handle auto-open from email link
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const openShare = searchParams.get('openShare')
        const requesterParam = searchParams.get('requester')

        if (openShare === 'true') {
            setOpen(true)

            // If there's a requester, set to individual mode and pre-populate
            if (requesterParam) {
                setIsFromAccessRequest(true)
                setRequesterAccountId(requesterParam)
                setSharingType('individual')

                // Find the requester in the members list
                const requester = membersData.find(member => member.id === requesterParam)

                if (requester) {
                    setInviteList(prev => {
                        // Only add if not already in the list
                        if (prev.find(invite => invite.id === requesterParam)) {
                            return prev
                        }
                        return [
                            ...prev,
                            {
                                id: requester.id,
                                name: requester.user?.fullname || 'User',
                                email: requester.email,
                                role: 'viewer',
                            },
                        ]
                    })
                }
            }
        }
    }, [location.search, membersData])

    const getCurrentSharingStatus = () => {
        if (sharingType === 'none') {
            return (
                <div className='flex items-center gap-2 text-sm text-[#313131] border rounded-lg p-3 bg-gray-50'>
                    <div className='flex items-center gap-2'>
                        <Lock className='w-4 h-4 text-[#63316B]' />
                        <span>Not shared with anyone</span>
                    </div>
                </div>
            )
        }

        if (sharingType === 'workspace') {
            return (
                <div className='flex items-center gap-2 p-3 text-sm text-white border rounded-lg bg-primary-main'>
                    <div className='flex items-center gap-2'>
                        <Users2 className='w-4 h-4 text-white' />
                        <span>
                            Shared with entire workspace as <span className='font-medium'>{workspaceRole}s</span>
                        </span>
                    </div>
                </div>
            )
        }

        if (sharingType === 'individual') {
            const sharedWithCount = inviteList.length
            if (sharedWithCount > 0) {
                return (
                    <div className='space-y-2'>
                        <div className='flex items-center justify-between p-3 text-sm text-white border rounded-lg bg-primary-main'>
                            <div className='flex items-center gap-2'>
                                <Users className='w-4 h-4 text-white' />
                                <span>
                                    Shared with{' '}
                                    <span className='font-medium'>
                                        {sharedWithCount} {sharedWithCount === 1 ? 'person' : 'people'}
                                    </span>
                                </span>
                            </div>
                            <button
                                type='button'
                                onClick={() => setShowShareDetails(!showShareDetails)}
                                className='flex items-center gap-1 text-white hover:opacity-80'
                            >
                                <Eye className='w-4 h-4' />
                                {showShareDetails ? (
                                    <ChevronUp className='w-4 h-4' />
                                ) : (
                                    <ChevronDown className='w-4 h-4' />
                                )}
                            </button>
                        </div>

                        {showShareDetails && (
                            <div className='p-3 space-y-2 border rounded-lg bg-gray-50'>
                                {inviteList.map((invite, index) => {
                                    return (
                                        <div key={index} className='flex items-center justify-between text-sm'>
                                            <div>
                                                <p className='font-medium text-[#313131]'>{invite?.name}</p>
                                                <p className='text-xs text-gray-500'>{invite.email}</p>
                                            </div>
                                            <span className='text-[#63316B]'>{capitalCase(invite.role)}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        )}
                    </div>
                )
            }
        }
    }

    const handleResetSharing = () => {
        setSharingType('none')
        setInviteList([])
        setValue('accounts', [])
        setWorkspaceRole('viewer')
        setForceInherit(false)
        setShowShareDetails(false)
    }

    const handleOpenChange = (newOpen: boolean) => {
        setOpen(newOpen)

        // Clean up URL parameters when closing the modal
        if (!newOpen) {
            const searchParams = new URLSearchParams(location.search)
            if (searchParams.has('openShare') || searchParams.has('requester')) {
                searchParams.delete('openShare')
                searchParams.delete('requester')
                const newSearch = searchParams.toString()
                const newUrl = `${location.pathname}${newSearch ? `?${newSearch}` : ''}`
                navigate(newUrl, { replace: true })
            }

            // Reset access request state
            setIsFromAccessRequest(false)
            setRequesterAccountId(null)

            // Call onClose callback if provided
            onClose?.()
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild className={insideDropdown ? 'w-full' : ''}>{trigger}</DialogTrigger>
            <DialogContent className='max-w-[600px] min-h-[600px]'>
                <DialogHeader>
                    <DialogTitle>
                        Share {document.data_type === 'info' || !document.data_type ? 'document' : document.data_type}{' '}
                        with others
                    </DialogTitle>
                </DialogHeader>

                {/* Access Request Banner */}
                {isFromAccessRequest && (
                    <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
                        <div className='flex gap-3'>
                            <AlertCircle className='w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5' />
                            <div className='flex-1'>
                                <p className='text-sm font-semibold text-blue-900 mb-1'>Access Request</p>
                                <p className='text-sm text-blue-800'>
                                    {inviteList.find(invite => invite.id === requesterAccountId)?.name || 'Someone'} has
                                    requested access to this document. They have been pre-selected below with "Viewer"
                                    access.
                                </p>
                                <p className='text-sm text-blue-700 mt-2 font-medium'>
                                    → Review the access level and click "Save access settings" to grant access.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {getCurrentSharingStatus()}

                {document.isFolder && (
                    <div className='bg-[#F9E7FF]/20 rounded-lg p-4'>
                        <div className='flex gap-2'>
                            <Info className='w-5 h-5 text-[#63316B] flex-shrink-0' />
                            <p className='text-sm text-[#313131]'>
                                <span className='font-medium'>Permission Inheritance:</span> All files and folders
                                within this folder will inherit these permissions, except those with their own specific
                                permissions (and their children).
                            </p>
                        </div>
                    </div>
                )}

                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col h-full'>
                        <div className='max-w-[600px] space-y-6 flex-1'>
                            <div className='flex justify-end'>
                                <Button
                                    type='button'
                                    variant='ghost'
                                    onClick={handleResetSharing}
                                    className='text-sm text-[#63316B] hover:text-[#63316B]/80 h-auto py-1 px-2'
                                >
                                    Remove all access
                                </Button>
                            </div>

                            <RadioGroup
                                value={sharingType}
                                onValueChange={(value: 'workspace' | 'individual' | 'none') => {
                                    setSharingType(value)
                                    setInviteList([])
                                    setValue('accounts', [])
                                }}
                                className='grid grid-cols-2 gap-4'
                            >
                                <Label
                                    htmlFor='workspace-option'
                                    className={`border rounded-lg p-4 cursor-pointer ${
                                        sharingType === 'workspace' ? 'border-[#63316B] bg-[#F9E7FF]/20' : ''
                                    }`}
                                >
                                    <RadioGroupItem id='workspace-option' value='workspace' />
                                    <div className='text-sm font-medium'>Workspace Access</div>
                                    <div className='text-xs text-muted-foreground'>
                                        Share with all workspace members
                                    </div>
                                </Label>
                                <Label
                                    htmlFor='individual-option'
                                    className={`border rounded-lg p-4 cursor-pointer ${
                                        sharingType === 'individual' ? 'border-[#63316B] bg-[#F9E7FF]/20' : ''
                                    }`}
                                >
                                    <RadioGroupItem id='individual-option' value='individual' />
                                    <div className='text-sm font-medium'>Select People</div>
                                    <div className='text-xs text-muted-foreground'>
                                        Choose specific people to share with
                                    </div>
                                </Label>
                            </RadioGroup>

                            <div className='min-h-min'>
                                {sharingType === 'workspace' && (
                                    <div className='space-y-4'>
                                        <div className='p-4 border rounded-lg'>
                                            <div className='flex items-center justify-between'>
                                                <div>
                                                    <p className='text-sm text-[#313131] font-medium'>
                                                        Access level for workspace
                                                    </p>
                                                    <p className='text-xs text-[#6b7280]'>
                                                        All members will get the same access
                                                    </p>
                                                </div>
                                                <Select
                                                    defaultValue='viewer'
                                                    value={workspaceRole}
                                                    onValueChange={setWorkspaceRole}
                                                >
                                                    <SelectTrigger className='w-[140px]'>
                                                        <SelectValue placeholder='Select Access' />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {fileAccessRoles.map((role, index) => (
                                                            <SelectItem value={role} key={index}>
                                                                {capitalCase(role)}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <div className='p-4 bg-[#F9E7FF]/20 rounded-lg'>
                                            <div className='flex items-start gap-3'>
                                                <div className='mt-1'>
                                                    {workspaceRole === 'viewer' ? (
                                                        <Eye size={20} />
                                                    ) : (
                                                        <Edit size={20} />
                                                    )}
                                                </div>
                                                <div>
                                                    <p className='text-sm font-medium text-[#63316B]'>
                                                        {`Workspace members will be ${workspaceRole}s`}
                                                    </p>
                                                    <p className='text-xs text-[#6b7280]'>
                                                        {getRoleDescription(workspaceRole)}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {sharingType === 'individual' && (
                                    <>
                                        <div className='grid gap-4'>
                                            <div className='flex flex-col gap-2'>
                                                <Label htmlFor='title'>Share with</Label>
                                                <Controller
                                                    name='accounts'
                                                    shouldUnregister={false}
                                                    control={control}
                                                    defaultValue={[]}
                                                    render={({ field }) => (
                                                        <SelectWithTags
                                                            {...field}
                                                            createable={false}
                                                            value={[]}
                                                            placeholder='Select members to share document with'
                                                            options={(membersData || []).map(member => ({
                                                                label: member.user?.fullname || 'User',
                                                                value: member.id,
                                                                email: member.email,
                                                            }))}
                                                            onChange={value => {
                                                                field.onChange([])

                                                                setInviteList(prev => {
                                                                    return [
                                                                        ...prev,
                                                                        ...value
                                                                            .filter(
                                                                                (invitee: any) =>
                                                                                    !prev.find(
                                                                                        prevInvitee =>
                                                                                            prevInvitee.id ===
                                                                                            invitee.value
                                                                                    )
                                                                            )
                                                                            .map((invitee: any) => {
                                                                                return {
                                                                                    id: invitee.value,
                                                                                    name: invitee.label,
                                                                                    email: invitee.email,
                                                                                    role: 'viewer',
                                                                                }
                                                                            }),
                                                                    ]
                                                                })
                                                            }}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>

                                        <div className='max-w-[600px] min-h-max flex flex-col gap-2 overflow-y-auto mt-4'>
                                            {inviteList.map((invite, index) => {
                                                const isRequester = invite.id === requesterAccountId
                                                return (
                                                    <div
                                                        key={invite.id}
                                                        className={`flex items-center justify-between p-3 border rounded-lg ${
                                                            isRequester
                                                                ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-200'
                                                                : ''
                                                        }`}
                                                    >
                                                        <div className='flex-1'>
                                                            <div className='flex items-center gap-2'>
                                                                <p className='text-sm font-medium text-[#313131]'>
                                                                    {invite?.name}
                                                                </p>
                                                                {isRequester && (
                                                                    <span className='px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-100 rounded-full'>
                                                                        Requested Access
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className='text-xs text-gray-600'>{invite.email}</p>
                                                        </div>
                                                        <div className='flex items-center gap-3'>
                                                            <Select
                                                                defaultValue={invite.role}
                                                                onValueChange={value => {
                                                                    handleRoleChange(index, value)
                                                                }}
                                                            >
                                                                <SelectTrigger className='w-full'>
                                                                    <SelectValue placeholder='Select Role' />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {fileAccessRoles.map((role, index) => (
                                                                        <SelectItem value={role} key={index}>
                                                                            {capitalCase(role)}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            <button
                                                                type='button'
                                                                onClick={() =>
                                                                    setInviteList(currentList =>
                                                                        currentList.filter((_, i) => i !== index)
                                                                    )
                                                                }
                                                                className='text-gray-400 hover:text-gray-600'
                                                            >
                                                                <X size={20} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            {document.isFolder && (
                                <div className='bg-[#F9E7FF]/20 rounded-lg p-4'>
                                    <div className='flex gap-2'>
                                        <div className='flex-shrink-0 w-5'>
                                            <input
                                                type='checkbox'
                                                id='forceInherit'
                                                className='rounded border-gray-300 text-[#63316B] focus:ring-[#63316B]'
                                                checked={forceInherit}
                                                onChange={() => setForceInherit(!forceInherit)}
                                            />
                                        </div>
                                        <label htmlFor='forceInherit' className='text-sm text-[#313131]'>
                                            Force all children to inherit these permissions (overrides existing
                                            individual permissions)
                                        </label>
                                    </div>
                                </div>
                            )}

                            <DialogFooter>
                                <div className='flex justify-end gap-4'>
                                    <Button
                                        disabled={sharingType === 'individual' ? !isValid : false}
                                        className='bg-[#63316B] disabled:#F9E7FF'
                                        type='submit'
                                        loading={sharing}
                                    >
                                        Save access settings
                                    </Button>
                                </div>
                            </DialogFooter>
                        </div>
                    </form>
                </FormProvider>
            </DialogContent>
        </Dialog>
    )
}

export default ShareFile
